import DeleteIcon from '@mui/icons-material/Delete';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import WorkIcon from '@mui/icons-material/Work';
import { alpha, Box, Grid, MenuItem, Typography, useTheme } from '@mui/material';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { FadingBalls } from 'react-cssfx-loading';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useDeleteProject } from '@/api/mutations/useProjectMutations';
import { actsAtom } from '@/atoms/acts';
import { Image } from '@/components/ImageAvatar/ImageAvatar';
import { styles } from '@/components/StyledComponents';
import { Popover } from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { Tooltip } from '@/components/ui/tooltip';
import { createAct, episodeActs as defaultEpisodeActs, paths, STORAGE } from '@/constants';
import { useProjectsList } from '@/data/entities/projects';
import { assetCategory, textAtomTypeId } from '@/enums';
import { useColorMode, useRequiredAppSelector, useVersion } from '@/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useWorkspace } from '@/hooks/useWorkspace';
import { modals } from '@/modals';
import { usePrefValue } from '@/preferences';
import { ProjectHeaderText, TwoArrowGrid } from './NavigationProject.styles';

interface props {
    setOpen?: (state: string) => void;
}

export const NavigationProject = ({ setOpen }: props) => {
    const [projectsMenuAnchor, setProjectsMenuAnchor] = useState<HTMLElement | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isHover, setHover] = useState(0);

    const projectId = useRequiredAppSelector('projectId');
    const companyId = useRequiredAppSelector('companyId');
    const { canDeleteProject } = usePermissions();
    const { mutateAsync: deleteProject } = useDeleteProject();
    const version = useVersion();
    const side_bar = usePrefValue('side_bar');
    const setActs = useSetAtom(actsAtom);
    const theme = useTheme();
    const isDarkMode = useColorMode();

    const { data: projects, isFetching: fetchingProjects } = useProjectsList({ companyId, version }, { enabled: !!companyId });
    const project = projects?.find(el => el.id === projectId);
    const switchWorkspace = useWorkspace();

    const handleDeleteProject = () => {
        modals.confirm({
            message: t('project.delete-project'),
            cancelLabel: t('buttons.no-delete'),
            // A refusal throws: the dialog stays open and the host shows it.
            onConfirm: async () => {
                await deleteProject({ project_id: projectId });
                switchWorkspace({ companyID: companyId });
                navigate(paths.company.projects);
            },
        });
    };

    useEffect(() => {
        const tbdAct = defaultEpisodeActs.find(act => act.typeId === textAtomTypeId.episode_act_tbd);
        if (project?.act_quantity && tbdAct) {
            const newActs = Array.from(Array(project.act_quantity).keys()).map(value => createAct(value + 1));
            setActs([...newActs, tbdAct]);
        }
    }, [project?.act_quantity, setActs]);

    const ProjectActions = () => (
        <Grid borderRadius="8px" overflow="hidden" container sx={{ background: theme => theme.palette.secondary.light }}>
            <Grid
                sx={[
                    styles.iconTextRowMenu,
                    {
                        '& svg': {
                            fill: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                        },
                    },
                ]}
                container
                onClick={() => navigate(paths.project.index)}
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M5.74992 2.625C5.74992 2.27983 5.4701 2 5.12492 2C4.77975 2 4.49992 2.27983 4.49992 2.625V4.49996H2.625C2.27983 4.49996 2 4.77978 2 5.12496C2 5.47013 2.27983 5.74996 2.625 5.74996H4.49992V7.625C4.49992 7.97017 4.77975 8.25 5.12492 8.25C5.4701 8.25 5.74992 7.97017 5.74992 7.625V5.74996H7.625C7.97017 5.74996 8.25 5.47013 8.25 5.12496C8.25 4.77978 7.97017 4.49996 7.625 4.49996H5.74992V2.625Z"
                        fill="auto"
                    />
                    <path
                        d="M16.9149 13.3773C16.6667 13.6255 16.2643 13.6255 16.0161 13.3773L15.8759 13.2371L14.5187 15.6906C14.3141 16.0605 13.9561 16.3341 13.5575 16.4253L6.67415 18L9.65397 15.0202C10.3852 15.4521 11.3428 15.3539 11.9711 14.7256C12.7158 13.9809 12.7158 12.7736 11.9711 12.0289C11.2265 11.2843 10.0191 11.2843 9.27441 12.0289C8.64614 12.6572 8.54794 13.6148 8.9798 14.3461L6 17.3258L7.57475 10.4425C7.66593 10.044 7.93955 9.68603 8.30941 9.48143L10.7629 8.12418L10.6227 7.98395C10.3744 7.73573 10.3744 7.33328 10.6227 7.08505L11.5215 6.18617C11.7698 5.93795 12.1722 5.93795 12.4205 6.18617L17.8138 11.5795C18.0621 11.8277 18.0621 12.2302 17.8138 12.4784L16.9149 13.3773Z"
                        fill="auto"
                    />
                </svg>

                <Typography
                    children={project?.title || t('profile.profile')}
                    color="text.primary"
                    variant="body2"
                    sx={styles.textHoverMenu}
                />
            </Grid>
            <Grid
                sx={[styles.iconTextRowMenu]}
                container
                onClick={e => {
                    e.stopPropagation();
                    navigate(paths.company.projects);
                }}
            >
                <ImportExportIcon
                    sx={{
                        transform: 'rotate(90deg)',
                        color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                        fontSize: '1.25rem',
                    }}
                />
                <Text variant="body2" color="text-text-primary" children={t('project.change-project')} />
            </Grid>
            {canDeleteProject && (
                <Grid sx={[styles.iconTextRowMenu]} container onClick={handleDeleteProject}>
                    <DeleteIcon
                        sx={{
                            color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                            fontSize: '1.25rem',
                        }}
                    />
                    <Text variant="body2" color="text-text-primary" children={t('project.delete-project')} />
                </Grid>
            )}
        </Grid>
    );

    return (
        <Grid
            container
            gap="4px"
            flexWrap="nowrap"
            alignItems="center"
            sx={[{ position: 'relative' }, !side_bar && isHover !== 2 && { maxWidth: '40px' }]}
        >
            <Grid
                container
                gap="16px"
                alignItems="center"
                padding="6px 8px 6px 2px"
                flexWrap="nowrap"
                minHeight="48px"
                onMouseEnter={() => setHover(1)}
                onMouseLeave={() => setHover(0)}
                sx={[
                    { cursor: 'pointer', borderRadius: '8px' },
                    side_bar &&
                        (location.pathname === paths.project.index || isHover === 1) && {
                            background: theme => alpha(theme.palette.secondary.light, 0.04),
                        },
                    !isDarkMode &&
                        location.pathname === paths.project.index &&
                        side_bar && {
                            background: theme => alpha(theme.palette.secondary.main, 0.1),
                        },
                    !isDarkMode &&
                        isHover === 1 &&
                        side_bar && {
                            background: theme => theme.custom.c7,
                        },
                ]}
                onClick={() => {
                    if (location.pathname !== paths.project.index) navigate(paths.project.index);
                    setOpen?.('project');
                }}
            >
                <Tooltip
                    title={<ProjectActions />}
                    placement="right-start"
                    sideOffset={4}
                    contentClassName="p-0 rounded-lg overflow-hidden bg-bg-paper"
                >
                    <div onMouseEnter={() => setHover(2)} onMouseLeave={() => setHover(0)} className="flex items-center">
                        <Image
                            cursor="pointer"
                            width="32px"
                            height="32px"
                            margin="0"
                            border={theme => (isHover === 20 ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent')}
                            src={
                                project?.assets?.some(item => item.category === assetCategory.background)
                                    ? STORAGE + (project.assets.find(item => item.category === assetCategory.background)?.path ?? '')
                                    : ''
                            }
                            alt={project ? project.title : ' '}
                            title={project ? project.title : 'profile'}
                            children={
                                <WorkIcon
                                    sx={{
                                        color: theme => (isDarkMode ? 'inherit' : theme.custom.c22),
                                        fontSize: '1.25rem',
                                    }}
                                />
                            }
                            sx={{
                                opacity: location.pathname === paths.project.index || isHover !== 0 ? 1 : 0.6,
                            }}
                            backgroundColor={
                                isHover !== 0 || location.pathname === paths.project.index ? theme.palette.text.secondary : theme.custom.c5
                            }
                        />
                    </div>
                </Tooltip>

                {side_bar && (
                    <div className="gap-[8px]">
                        <ProjectHeaderText children={t('project.project')} />
                        <Typography
                            children={project ? project.title : ' '}
                            variant="body2"
                            color="primary.contrastText"
                            sx={[
                                styles.textHoverMenu,
                                isHover === 1 && {
                                    color: theme => (isDarkMode ? theme.palette.secondary.main : theme.custom.c22),
                                },
                                isHover !== 1 && !isDarkMode && { color: theme => theme.custom.c22 },
                                location.pathname === paths.project.index && {
                                    color: theme => theme.palette.secondary.main,
                                },
                            ]}
                        />
                    </div>
                )}
            </Grid>

            {side_bar && (
                <>
                    <TwoArrowGrid
                        container
                        sx={[
                            !!projectsMenuAnchor && {
                                background: theme => (isDarkMode ? alpha(theme.palette.secondary.light, 0.04) : theme.custom.c7),
                            },
                        ]}
                        onClick={event => setProjectsMenuAnchor(event.currentTarget)}
                    >
                        <ImportExportIcon
                            sx={{
                                transform: 'rotate(90deg)',
                                '&:hover': { cursor: 'pointer' },
                            }}
                            color={projectsMenuAnchor ? 'secondary' : 'info'}
                        />
                    </TwoArrowGrid>
                    <Popover
                        anchorEl={projectsMenuAnchor}
                        open={!!projectsMenuAnchor}
                        onClose={() => setProjectsMenuAnchor(null)}
                        side="bottom"
                        align="start"
                        className="shadow-none rounded-lg flex flex-col overflow-hidden bg-bg-paper"
                    >
                        <Box
                            sx={[
                                {
                                    gap: '8px',
                                    borderRadius: '8px',
                                    minHeight: '0',
                                    overflowY: 'auto',
                                },
                            ]}
                        >
                            {fetchingProjects ? (
                                <FadingBalls color="text.primary" width="40px" height="10px" duration="0.3s" />
                            ) : (
                                <Grid
                                    container
                                    sx={{
                                        background: theme => (isDarkMode ? theme.palette.secondary.light : theme.custom.c7),
                                        padding: '16px',
                                        flexWrap: 'nowrap',
                                        overflowY: 'auto',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Text variant="body1" color="text-inherit" children={t('project.change-project')} className="p-[8px]" />

                                    {projects?.length ? (
                                        projects.map(item => (
                                            <MenuItem
                                                key={item.id}
                                                value={item.title}
                                                sx={styles.textOneLine}
                                                onClick={() => {
                                                    setProjectsMenuAnchor(null);
                                                    navigate(paths.project.index);
                                                    switchWorkspace({ projectID: item.id });
                                                    setHover(0);
                                                }}
                                            >
                                                {item.title}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <Text variant="body1" children={t('company.no-companies')} />
                                    )}
                                </Grid>
                            )}
                        </Box>
                    </Popover>
                </>
            )}
        </Grid>
    );
};
